<template>
  <div class="mt-1">
    <slot :exchange-rate="formattedExchangeRate" />
    <p
      class="text-12 lg:text-14"
      :class="percentageDiff < -0.05 ? 'worse' : percentageDiff >= 0 ? 'better' : 'same'"
      v-html="rateComparisonTranslation"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Service } from './types'

const props = defineProps<{
  service: Service
  midMarketRate: number
  showMidMarket?: boolean
}>()

const { $lang, $t } = useNuxtApp()

const exchangeRate = computed(() => {
  return props.service.promotion?.rate ?? props.service.quote.rate
})

const formattedExchangeRate = computed(() => {
  return formatRate(exchangeRate.value, $lang)
})

const percentageDiff = computed(() => {
  return Number((100 * (exchangeRate.value / props.midMarketRate - 1)).toFixed(2))
})

const rateComparisonTranslation = computed(() => {
  const midMarketRate = props.showMidMarket ? formatRate(props.midMarketRate, $lang) : ''
  if (percentageDiff.value === 0) {
    return $t('transfer_comparison.same_as_mid_market', { midMarketRate })
  }
  if (percentageDiff.value > 0) {
    return $t('transfer_comparison.better_than_mid_market', {
      delta: formatNumber(Math.abs(percentageDiff.value), $lang, { maximumFractionDigits: 2 }),
      midMarketRate,
    })
  }
  return $t('transfer_comparison.worse_than_mid_market', {
    delta: formatNumber(Math.abs(percentageDiff.value), $lang, { maximumFractionDigits: 2 }),
    midMarketRate,
  })
})
</script>

<style scoped>
.better :deep(strong) {
  @apply text-green-800;
}

.same :deep(strong) {
  @apply text-gray-700;
}

.worse :deep(strong) {
  @apply text-red-900 font-normal;
}
</style>
